import React from 'react';
import type {LucideIcon} from 'lucide-react';
import {
    AlignHorizontalJustifyCenterIcon,
    AlignHorizontalJustifyEndIcon,
    AlignHorizontalJustifyStartIcon,
    AlignHorizontalSpaceAroundIcon,
    AlignVerticalJustifyCenterIcon,
    AlignVerticalJustifyEndIcon,
    AlignVerticalJustifyStartIcon,
    AlignVerticalSpaceAroundIcon,
    AppWindowIcon,
    BringToFrontIcon,
    CopyPlusIcon,
    EraserIcon,
    GroupIcon,
    LayerArrowDownIcon,
    LayerArrowUpIcon,
    RefreshCwIcon,
    SendToBackIcon,
    UngroupIcon
} from 'lucide-react';
import {DistributeDirection, ObjectAlignment, ObjectTypes} from './types';
import {ToolButton, Toolbar, ToolbarDivider} from '../ds/ToolFrame.tsx';

const alignmentOptions: {
    title: string;
    alignment: ObjectAlignment;
    icon: LucideIcon;
}[] = [
    {title: 'Align left', alignment: 'left', icon: AlignHorizontalJustifyStartIcon},
    {
        title: 'Align horizontal centre',
        alignment: 'centerX',
        icon: AlignHorizontalJustifyCenterIcon
    },
    {title: 'Align right', alignment: 'right', icon: AlignHorizontalJustifyEndIcon},
    {title: 'Align top', alignment: 'top', icon: AlignVerticalJustifyStartIcon},
    {
        title: 'Align vertical centre',
        alignment: 'centerY',
        icon: AlignVerticalJustifyCenterIcon
    },
    {title: 'Align bottom', alignment: 'bottom', icon: AlignVerticalJustifyEndIcon}
];

const distributeOptions: {
    title: string;
    direction: DistributeDirection;
    icon: LucideIcon;
}[] = [
    {
        title: 'Distribute horizontally',
        direction: 'horizontal',
        icon: AlignHorizontalSpaceAroundIcon
    },
    {
        title: 'Distribute vertically',
        direction: 'vertical',
        icon: AlignVerticalSpaceAroundIcon
    }
];

interface EditToolbarProps {
    onRemoveSelected: () => void;
    onDuplicate: () => void;
    onBringForward: () => void;
    onBringBackwards: () => void;
    onToFront: () => void;
    onSendToBack: () => void;
    onGroupItems: () => void;
    onUngroupItems: () => void;
    onAlign: (alignment: ObjectAlignment) => void;
    onDistribute: (direction: DistributeDirection) => void;
    controlsDisabled: boolean;
    activeSelection: string | null;
    activeObjectMeta: string | null | undefined;
    /*
     * Only supplied where a legacy project card can be selected. Its
     * presence is what shows the upgrade button.
     */
    onRefreshProjectCard?: () => void;
    // Picks which tab the selected project card opens.
    onChooseProjectCardTab?: () => void;
}

// What you can do with the selected object(s).
const EditToolbar: React.FC<EditToolbarProps> = ({
    onRemoveSelected,
    onDuplicate,
    onBringForward,
    onBringBackwards,
    onToFront,
    onSendToBack,
    onGroupItems,
    onUngroupItems,
    onAlign,
    onDistribute,
    controlsDisabled,
    activeSelection,
    activeObjectMeta,
    onRefreshProjectCard,
    onChooseProjectCardTab
}) => {
    return (
        <Toolbar label="Selection" className="whiteboard-toolbar">
            <ToolButton
                label="Delete"
                shortcut="Del"
                icon={EraserIcon}
                onClick={onRemoveSelected}
                disabled={controlsDisabled}
            />
            <ToolButton
                label="Duplicate"
                shortcut="Ctrl+D"
                icon={CopyPlusIcon}
                onClick={onDuplicate}
                disabled={controlsDisabled}
            />
            <ToolbarDivider />
            <ToolButton
                label="Bring Forward"
                shortcut="Ctrl+]"
                icon={LayerArrowUpIcon}
                onClick={onBringForward}
                disabled={controlsDisabled}
            />
            <ToolButton
                label="Send Backward"
                shortcut="Ctrl+["
                icon={LayerArrowDownIcon}
                onClick={onBringBackwards}
                disabled={controlsDisabled}
            />
            <ToolButton
                label="Bring to Front"
                shortcut="Ctrl+Shift+]"
                icon={BringToFrontIcon}
                onClick={onToFront}
                disabled={controlsDisabled}
            />
            <ToolButton
                label="Send to Back"
                shortcut="Ctrl+Shift+["
                icon={SendToBackIcon}
                onClick={onSendToBack}
                disabled={controlsDisabled}
            />
            {activeSelection == ObjectTypes.ActiveSelection && (
                <>
                    <ToolbarDivider />
                    <ToolButton
                        label="Group Items"
                        shortcut="Ctrl+G"
                        icon={GroupIcon}
                        onClick={onGroupItems}
                        disabled={controlsDisabled}
                    />
                    {alignmentOptions.map(option => (
                        <ToolButton
                            key={option.alignment}
                            label={option.title}
                            icon={option.icon}
                            onClick={() => onAlign(option.alignment)}
                            disabled={controlsDisabled}
                        />
                    ))}
                    {distributeOptions.map(option => (
                        <ToolButton
                            key={option.direction}
                            label={option.title}
                            icon={option.icon}
                            onClick={() => onDistribute(option.direction)}
                            disabled={controlsDisabled}
                        />
                    ))}
                </>
            )}

            {activeSelection == ObjectTypes.Group &&
                activeObjectMeta !== 'fixed-group' && (
                    <>
                        <ToolbarDivider />
                        <ToolButton
                            label="Ungroup Items"
                            shortcut="Ctrl+Shift+G"
                            icon={UngroupIcon}
                            onClick={onUngroupItems}
                            disabled={controlsDisabled}
                        />
                    </>
                )}

            {activeSelection == ObjectTypes.ProjectCard &&
                onChooseProjectCardTab && (
                    <>
                        <ToolbarDivider />
                        <ToolButton
                            label="Open at…"
                            icon={AppWindowIcon}
                            onClick={onChooseProjectCardTab}
                            disabled={controlsDisabled}
                        />
                    </>
                )}

            {/*
             * A project card from before the card was its own element type.
             * Replacing it in place is opt-in, so existing boards are never
             * rewritten behind anyone's back.
             */}
            {activeSelection == ObjectTypes.Group &&
                activeObjectMeta === 'fixed-group' &&
                onRefreshProjectCard && (
                    <>
                        <ToolbarDivider />
                        <ToolButton
                            label="Refresh card"
                            icon={RefreshCwIcon}
                            onClick={onRefreshProjectCard}
                            disabled={controlsDisabled}
                        />
                    </>
                )}
        </Toolbar>
    );
};

export default EditToolbar;
